import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  getSocialProofDisplay,
  parseSocialProofText,
  resolveSocialProofMetrics,
  normalizeSocialProofStyle,
  SOCIAL_PROOF_STYLE,
  SOCIAL_PROOF_ACCENT,
} from "../../../../shared/product-block-display.js";
import styles from "./AiSocialProofBar.module.css";

function buildAvatarColors() {
  return [
    "var(--we-primary, #6366f1)",
    "color-mix(in srgb, var(--we-primary, #6366f1) 75%, #8b5cf6)",
    "color-mix(in srgb, var(--we-primary, #6366f1) 55%, #ec4899)",
    "color-mix(in srgb, var(--we-primary, #6366f1) 45%, #14b8a6)",
    "color-mix(in srgb, var(--we-primary, #6366f1) 35%, #f59e0b)",
  ];
}

function LiveDot() {
  return <span className={styles.liveDot} aria-hidden />;
}

function AvatarStack({ count = 4 }) {
  const colors = buildAvatarColors();
  const n = Math.max(2, Math.min(5, Number(count) || 4));
  return (
    <span className={styles.avatars} aria-hidden>
      {colors.slice(0, n).map((color, i) => (
        <span
          key={`${color}-${i}`}
          className={styles.avatar}
          style={{ backgroundColor: color, zIndex: n - i }}
        />
      ))}
    </span>
  );
}

function MetricBlock({ count, label, className = "" }) {
  if (!count && !label) return null;
  return (
    <div className={`${styles.metricBlock} ${className}`.trim()}>
      {count ? <span className={styles.metricCount}>{count}</span> : null}
      {label ? <span className={styles.metricLabel}>{label}</span> : null}
    </div>
  );
}

function SplitMetricsLayout({ metrics, display }) {
  const { viewing, purchase } = metrics;
  return (
    <div className={styles.splitInner}>
      {display.showLiveDot !== false ? <LiveDot /> : null}
      {display.showAvatars !== false ? (
        <AvatarStack count={display.avatarCount} />
      ) : null}
      <div className={styles.splitMetrics}>
        {viewing?.count || viewing?.label ? (
          <MetricBlock count={viewing.count} label={viewing.label} />
        ) : null}
        {viewing && purchase && (viewing.count || viewing.label) && (purchase.count || purchase.label) ? (
          <span className={styles.splitDivider} aria-hidden />
        ) : null}
        {purchase?.count || purchase?.label ? (
          <MetricBlock count={purchase.count} label={purchase.label} />
        ) : null}
      </div>
    </div>
  );
}

function InlinePillsLayout({ metrics, display }) {
  const pills = [];
  if (metrics.viewing?.count || metrics.viewing?.label) {
    pills.push({
      key: "viewing",
      icon: "👁",
      text: [metrics.viewing.count, metrics.viewing.label].filter(Boolean).join(" "),
    });
  }
  if (metrics.purchase?.count || metrics.purchase?.label) {
    pills.push({
      key: "purchase",
      icon: "🛒",
      text: [metrics.purchase.count, metrics.purchase.label].filter(Boolean).join(" "),
    });
  }
  return (
    <div className={styles.pillsRow}>
      {display.showLiveDot !== false ? <LiveDot /> : null}
      {pills.map((pill) => (
        <span key={pill.key} className={styles.pill}>
          {display.showIcon !== false ? (
            <span className={styles.pillIcon} aria-hidden>
              {pill.icon}
            </span>
          ) : null}
          <span className={styles.pillText}>{pill.text}</span>
        </span>
      ))}
    </div>
  );
}

function EmphasisRowLayout({ metrics, display, fallbackText }) {
  const parts = [];
  if (metrics.viewing?.count) {
    parts.push(
      <React.Fragment key="v">
        <strong className={styles.emphasisNum}>{metrics.viewing.count}</strong>{" "}
        {metrics.viewing.label || "viewing"}
      </React.Fragment>
    );
  }
  if (metrics.purchase?.count) {
    parts.push(
      <React.Fragment key="p">
        <strong className={styles.emphasisNum}>{metrics.purchase.count}</strong>{" "}
        {metrics.purchase.label || "bought"}
      </React.Fragment>
    );
  }
  const hasParts = parts.length > 0;
  return (
    <div className={styles.emphasisRow}>
      {display.showIcon !== false ? (
        <span className={styles.emphasisIcon} aria-hidden>
          ⚡
        </span>
      ) : null}
      {display.showLiveDot !== false ? <LiveDot /> : null}
      <span className={styles.emphasisText}>
        {hasParts
          ? parts.reduce((acc, part, i) => {
              if (i > 0) acc.push(<span key={`sep-${i}`}> · </span>);
              acc.push(part);
              return acc;
            }, [])
          : fallbackText}
      </span>
    </div>
  );
}

function AvatarsBarLayout({ metrics, display, fallbackText }) {
  const line =
    [
      metrics.viewing?.count && metrics.viewing?.label
        ? `${metrics.viewing.count} ${metrics.viewing.label}`
        : null,
      metrics.purchase?.count && metrics.purchase?.label
        ? `${metrics.purchase.count} ${metrics.purchase.label}`
        : null,
    ]
      .filter(Boolean)
      .join(" · ") || fallbackText;

  return (
    <>
      {display.showLiveDot !== false ? <LiveDot /> : null}
      {display.showAvatars !== false ? (
        <AvatarStack count={display.avatarCount} />
      ) : null}
      <span className={styles.text}>{line}</span>
    </>
  );
}

export default function AiSocialProofBar({ text, section, showAvatars: showAvatarsProp }) {
  const display = section ? getSocialProofDisplay(section) : {};
  const metrics = useMemo(
    () => (section ? resolveSocialProofMetrics(section) : parseFallback(text)),
    [section, text]
  );

  const fallbackText = String(text || section?.text || "").trim();
  const stylePreset = normalizeSocialProofStyle(display.style);
  const accent = display.accent || SOCIAL_PROOF_ACCENT.WARM;

  const hasContent =
    fallbackText ||
    metrics.viewing?.count ||
    metrics.viewing?.label ||
    metrics.purchase?.count ||
    metrics.purchase?.label;

  if (!hasContent) return null;

  const showAvatars =
    showAvatarsProp !== false && display.showAvatars !== false;

  const accentClass =
    accent === SOCIAL_PROOF_ACCENT.MINT
      ? styles.accentMint
      : accent === SOCIAL_PROOF_ACCENT.VIOLET
        ? styles.accentViolet
        : styles.accentWarm;

  let body = null;
  switch (stylePreset) {
    case SOCIAL_PROOF_STYLE.SPLIT_METRICS:
      body = <SplitMetricsLayout metrics={metrics} display={{ ...display, showAvatars }} />;
      break;
    case SOCIAL_PROOF_STYLE.INLINE_PILLS:
      body = <InlinePillsLayout metrics={metrics} display={display} />;
      break;
    case SOCIAL_PROOF_STYLE.EMPHASIS_ROW:
      body = (
        <EmphasisRowLayout
          metrics={metrics}
          display={display}
          fallbackText={fallbackText}
        />
      );
      break;
    case SOCIAL_PROOF_STYLE.TEXT_ONLY:
      body = <span className={styles.textOnly}>{fallbackText}</span>;
      break;
    case SOCIAL_PROOF_STYLE.AVATARS_BAR:
    default:
      body = (
        <AvatarsBarLayout
          metrics={metrics}
          display={{ ...display, showAvatars }}
          fallbackText={fallbackText}
        />
      );
  }

  return (
    <div
      className={`${styles.root} ${accentClass}`}
      data-social-proof-layout={stylePreset}
    >
      {body}
    </div>
  );
}

function parseFallback(text) {
  return parseSocialProofText(text);
}

AiSocialProofBar.propTypes = {
  text: PropTypes.string,
  section: PropTypes.object,
  showAvatars: PropTypes.bool,
};
